import { FormattedMessage, useIntl } from 'react-intl';
import { Spinner } from '../components/Spinner';
import { ToastWrap } from '../components/ToastWrap';
import { useEffect, useState } from 'react';
import * as settingsApi from '../api/Settings';
import { ISettings } from '../util/DataModelInterface';
import useToastHandler from '../hooks/useToastHandler';

interface ISettingValidation {
    billing_limit: boolean;
    accounting_email: boolean;
}

export const Settings = () => {
    const intl = useIntl();
    const { addToast } = useToastHandler();

    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);
    const [toastActive, setToastActive] = useState(false);
    const [toastHeader, setToastHeader] = useState("");
    const [toastText, setToastText] = useState("");
    const [toastType, setToastType] = useState("");
    const [settingState, setSettingState] = useState({
        billing_limit: "",
        accounting_email: "",
      } as ISettings);
      const [settingStateClass, setSettingStateClass] = useState({
        billing_limit: "",
        accounting_email: "",
      });

    useEffect(() => {
        setLoading(true);
        refreshData()
        .finally(() => {
            setLoading(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshData = async (force: boolean = false) => {
        // {"accounting_email": "sernet.test@gofore.com", ...}
        await settingsApi.getSettings(force).then((data: any) => {
            if (data?.error) {
                addToast(data.error, 'settings', 'fetch');
            } else if (data) {
                // console.log("settings: ", data);
                let set = {
                    billing_limit: data.billing_limit ? data.billing_limit : "",
                    accounting_email: data.accounting_email ? data.accounting_email : ""
                } as ISettings;
                setSettingState(set);
            }
        });
    }

    const isValidEmail = (email: string) => {
        // eslint-disable-next-line no-useless-escape
        const regex = new RegExp('^[^@<> ]+@[^@<> ]+\.[^@<> ]+$');
        return regex.test(String(email).toLowerCase())
    }

    const validationRules: ISettingValidation = {
        billing_limit: !!settingState.billing_limit && settingState.billing_limit.match(/^ *$/) === null,
        accounting_email: !!settingState.accounting_email && isValidEmail(settingState.accounting_email),
    }

    const update = (event: { currentTarget: any; }) => {
        const target = event.currentTarget
  
        setSettingState({
            ...settingState,
            [target.name]: target.type === 'checkbox'
                ? target.checked
                : target.value
        })
        setSettingStateClass({...settingStateClass, [target.name]: 'fieldchanged'});
        setChanged(true);
      }

    // Set states to saved
    const setSavedStates = () => {
        setSettingStateClass({
          billing_limit: 'fieldsaved',
          accounting_email: 'fieldsaved'
        });
        setToastHeader(intl.formatMessage({id: 'settings.saved'}));
        setToastText("");
        setToastType('success');
        setToastActive(true);
    }

    const saveSettings = async (e: { preventDefault: () => void; } | undefined) => {

        e?.preventDefault(); // needed?

        const isValidForm = Object
          .values(validationRules)
          .every(key => key)

        if (!isValidForm) {
            console.log('Not valid form');
            setToastHeader(intl.formatMessage({id: 'settings.saved.failed'}));
            setToastText(intl.formatMessage({id: 'settings.fail.info'}));
            setToastType('warning');
            setToastActive(true);
            return;
        }
  
        let settings =  {
          billing_limit: settingState.billing_limit,
          accounting_email: settingState.accounting_email
        } as ISettings;
  
        setLoading(true);
        setChanged(false);

        let resp = await settingsApi.modifySettings(settings);
        if (resp?.error) {
            // console.log("Error", resp.error);
            setToastHeader(intl.formatMessage({id: 'settings.saved.failed'}));
            setToastType('warning');
            setToastActive(true);
          } else {
            setSavedStates();
            await refreshData(true);
          }
        setLoading(false);
    }

    const validate = (field: keyof ISettingValidation) => {
        return (!validationRules[field] ? 'has-error' : '')
    }

    return (
        <>
        <Spinner active={loading} />
        <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
            <div className="action-buttons py-4 d-flex flex-row align-items-start">
                <div className="page-header">
                <h1><FormattedMessage id={'settings.title'}/></h1>
                <p className="info">
                    <i className="fa fa-info-circle me-2"></i>
                    <FormattedMessage id="settings.info"/>
                </p>
            </div>
                <div className="ms-auto d-flex flex-row align-items-center">
                    <button type="button" className="btn btn-primary btn-save" disabled={!changed}
                        onClick={(e) => saveSettings(e)}>
                        <i className="fa fa-save me-2"></i><FormattedMessage id="button.save"/></button>
                </div>
            </div>
            {!loading &&
                <div className="highlight_bg">

                    <div className={"form-group " + validate('billing_limit')}>
                        <label htmlFor="billing_limit" className="form-label"><FormattedMessage id="settings.billinglimit"/></label>
                        <input id="billing_limit" name="billing_limit" className={"form-control " + settingStateClass.billing_limit}
                        type="text" value={settingState.billing_limit} autoComplete="off"
                        onChange={update}/>
                        <div aria-atomic="true" aria-live="polite">
                        <div id="fielderror" className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="settings.obligatory.info"/>
                        </div>
                        </div>
                    </div>

                    <div className={"form-group " + validate('accounting_email')}>
                        <label htmlFor="accounting_email" className="form-label"><FormattedMessage id="settings.accountingemail"/></label>
                        <input id="accounting_email" name="accounting_email" className={"form-control " + settingStateClass.accounting_email}
                        type="text" value={settingState.accounting_email} autoComplete="off"
                        onChange={update}/>
                        <div aria-atomic="true" aria-live="polite">
                        <div id="fielderror" className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="settings.obligatory.info"/>
                        </div>
                        </div>
                    </div>
                </div>
            }
        </>
    );
};